import type { CSSProperties, ReactElement } from 'react';
import React, { Component } from 'react';

import { _get, arraysEqual } from '../utility/generic';
import interact from 'interactjs';
import type { Interactable } from '@interactjs/core/Interactable';
import type { TimelineGroupBase } from '../types';

export type SidebarProps<
  CustomGroup extends TimelineGroupBase = TimelineGroupBase,
> = {
  groups: CustomGroup[];
  width: number;
  height: number;
  headerHeight?: number;
  groupHeights: number[];
  keys: {
    groupIdKey: keyof CustomGroup;
    groupTitleKey: keyof CustomGroup;
    groupRightTitleKey: keyof CustomGroup;
  };
  groupRenderer?: React.ComponentType<{
    group: CustomGroup;
    isRightSidebar: boolean | undefined;
  }>;
  onResizeSidebar?: (
    isRightSidebar: boolean | undefined,
    pageX: number,
  ) => void;
  isRightSidebar?: boolean;
};

export default class Sidebar<
  CustomGroup extends TimelineGroupBase = TimelineGroupBase,
> extends Component<SidebarProps<CustomGroup>> {
  interact: Interactable | null;
  resizing: boolean | null;

  shouldComponentUpdate(nextProps: SidebarProps<CustomGroup>) {
    return !(
      nextProps.keys === this.props.keys &&
      nextProps.width === this.props.width &&
      nextProps.height === this.props.height &&
      nextProps.headerHeight === this.props.headerHeight &&
      arraysEqual(nextProps.groups, this.props.groups) &&
      arraysEqual(nextProps.groupHeights, this.props.groupHeights)
    );
  }

  setResizeRef = (element: HTMLDivElement | null) => {
    const { isRightSidebar } = this.props;
    if (element) {
      this.interact = interact(element)
        .resizable({
          edges: {
            left: isRightSidebar,
            right: !isRightSidebar,
            top: false,
            bottom: false,
          },
        })
        .on('resizestart', () => {
          this.resizing = true;
        })
        .on('resizemove', (e) => {
          if (this.resizing) {
            if (this.props.onResizeSidebar) {
              this.props.onResizeSidebar(isRightSidebar, e.pageX);
            }
          }
        })
        .on('resizeend', (e) => {
          if (this.resizing) {
            this.resizing = false;
            if (this.props.onResizeSidebar) {
              this.props.onResizeSidebar(isRightSidebar, e.pageX);
            }
          }
        });
    } else if (this.interact) {
      this.interact.unset();
      this.interact = null;
    }
  };

  renderGroupContent(
    group: CustomGroup,
    isRightSidebar: boolean | undefined,
    groupTitleKey: keyof CustomGroup,
    groupRightTitleKey: keyof CustomGroup,
  ): ReactElement | string {
    if (this.props.groupRenderer) {
      return React.createElement(this.props.groupRenderer, {
        group,
        isRightSidebar,
      });
    } else {
      return _get(group, isRightSidebar ? groupRightTitleKey : groupTitleKey);
    }
  }

  render() {
    const {
      width,
      groupHeights,
      height,
      isRightSidebar,
      headerHeight = 60,
      keys,
      groups,
    } = this.props;
    const { groupIdKey, groupTitleKey, groupRightTitleKey } = keys;

    const sidebarStyle: CSSProperties = {
      width: `${width}px`,
      height: `${height}px`,
      top: `${headerHeight}px`,
      [isRightSidebar ? 'right' : 'left']: 0,
      position: 'sticky',
      zIndex: 100,
    };

    const groupsStyle = {
      width: `${width}px`,
    };

    const groupLines = groups.map((group, index) => {
      const elementStyle = {
        height: `${groupHeights[index]}px`,
        lineHeight: `${groupHeights[index]}px`,
      };

      const key = _get(group, groupIdKey) as React.Key;
      return (
        <div
          key={key}
          className={
            'rct-sidebar-row rct-sidebar-row-' +
            (index % 2 === 0 ? 'even' : 'odd')
          }
          style={elementStyle}
        >
          {this.renderGroupContent(
            group,
            isRightSidebar,
            groupTitleKey,
            groupRightTitleKey,
          )}
        </div>
      );
    });

    return (
      <div
        className={'rct-sidebar' + (isRightSidebar ? ' rct-sidebar-right' : '')}
        style={sidebarStyle}
      >
        <div style={groupsStyle}>{groupLines}</div>
        <div
          ref={this.setResizeRef}
          className={
            'rct-sidebar-resize' +
            (isRightSidebar ? ' rct-sidebar-resize-right' : '')
          }
          style={isRightSidebar ? { left: 0 } : { right: 0 }}
        />
      </div>
    );
  }
}
